
import React, { useEffect, useState } from 'react';
import { supabase, fetchBlogs } from '../services/geminiService';
import { BlogPost } from '../types';
import { Calendar, ArrowRight, Play, Youtube, X, Activity } from 'lucide-react';

const BlogSection: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const data = await fetchBlogs();
      setBlogs(data);
      setLoading(false);
    };
    load();

    // REAL-TIME BLOG UPDATES
    const channel = supabase
      .channel('public-blogs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'blogs' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setBlogs(prev => [payload.new as BlogPost, ...prev]);
        } else if (payload.eventType === 'DELETE') {
          setBlogs(prev => prev.filter(b => b.id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
          setBlogs(prev => prev.map(b => b.id === payload.new.id ? (payload.new as BlogPost) : b));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getYouTubeEmbedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return null;
  };

  if (loading) return null;
  if (blogs.length === 0) return null;

  return (
    <section id="journal" className="py-24 bg-slate-950 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-2 drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]">Reflections</h2>
            <h3 id="journal-title" className="text-4xl md:text-5xl font-serif text-slate-50 flex items-center gap-4">
              Celestial Journal
              <Activity size={24} className="text-emerald-500 animate-pulse hidden md:block" />
            </h3>
            <div className="w-24 h-1 bg-amber-500 mt-6 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
          </div>
          <p className="text-indigo-300/60 max-w-md text-sm font-light leading-relaxed italic">
            "Musings on transits, archetypes, and the collective soul journey. Updated live as transmissions arrive."
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((post) => (
            <article 
              key={post.id}
              className="group bg-slate-900/60 backdrop-blur-md border border-indigo-900/50 rounded-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500 flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden relative">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                />
                
                {post.video_url && (
                  <button 
                    onClick={() => setActiveVideo(post.video_url || null)}
                    className="absolute inset-0 flex items-center justify-center group/play transition-all bg-slate-950/20 group-hover:bg-slate-950/0"
                  >
                    <div className="p-4 bg-rose-600 text-white rounded-full shadow-[0_0_20px_rgba(225,29,72,0.4)] group-hover/play:scale-110 transition-transform">
                      <Play size={24} fill="currentColor" />
                    </div>
                  </button>
                )}

                <div className="absolute top-4 left-4">
                   <div className="bg-slate-950/80 backdrop-blur-md border border-amber-500/20 px-3 py-1 rounded-full flex items-center gap-2">
                     <Calendar size={12} className="text-amber-500" />
                     <span className="text-[10px] text-white uppercase tracking-widest font-bold">
                       {new Date(post.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                     </span>
                   </div>
                </div>
              </div>
              
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] uppercase tracking-widest text-indigo-500 font-bold">Transmission by {post.author}</span>
                  {post.video_url && <span className="text-[9px] uppercase tracking-widest text-rose-500 font-bold flex items-center gap-1"><Youtube size={10}/> Video Included</span>}
                </div>
                <h4 className="text-2xl font-serif text-slate-50 mb-4 group-hover:text-amber-400 transition-colors">
                  {post.title}
                </h4>
                <p className="text-indigo-200/70 text-sm leading-relaxed mb-8 flex-1">
                  {post.excerpt}
                </p>
                <button className="flex items-center gap-2 text-amber-500 text-[10px] uppercase tracking-widest font-bold group/btn">
                  Read Transmission <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Video Overlay Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-12 bg-slate-950/95 backdrop-blur-xl animate-fade-in">
          <div className="relative w-full max-w-6xl aspect-video bg-black shadow-2xl rounded-sm overflow-hidden">
            <button 
              onClick={() => setActiveVideo(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-slate-900/80 text-white rounded-full hover:bg-rose-600 transition-all"
            >
              <X size={24} />
            </button>
            <iframe 
              className="w-full h-full"
              src={`${getYouTubeEmbedUrl(activeVideo)}?autoplay=1`}
              title="Cosmic Transmission"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default BlogSection;
